import CryptoJS, { enc } from "crypto-js"

export default class Crypto {
    static fromPEM(str:string) {
        const match = str.match(/\-{5}BEGIN.+?\-{5}\s([\w\d\s\+\/\,\=]+)\-{5}END.+\-{5}/)
        if (!match) throw "Invalid public key"
        return this.fromBase64(match[1])
    }

    static async toPEM(isPrivate:boolean, key:CryptoKey) {
        const exported = await window.crypto.subtle.exportKey(isPrivate ? "pkcs8" : "spki", key)
        const str = this.ab2str(exported)
        const type = isPrivate ? "PRIVATE" : "PUBLIC"
        return `-----BEGIN ${type} KEY-----\n${window.btoa(str)}\n-----END ${type} KEY-----`
    }

    static async sha256(text:string) {
        return this.toBase64(await window.crypto.subtle.digest("SHA-256", this.str2ab(text)))
    }
    static str2ab(text:string){
        return new TextEncoder().encode(text)
    }
    static ab2str(ab:ArrayBuffer|Uint8Array) {
        const array = ab instanceof ArrayBuffer ? Array.from(new Uint8Array(ab)) : ab
        return String.fromCharCode(...array)
    }
    static ab2hex(ab:ArrayBuffer|Uint8Array){
        const array = ab instanceof ArrayBuffer ? Array.from(new Uint8Array(ab)) : ab
        return Array.from(array, b => b.toString(16).padStart(2, "0")).join("")
    }
    static toBase64(text:string|ArrayBuffer){
        if (typeof(text)=="string") return window.btoa(text)
        else return window.btoa(this.ab2str(text))
    }
    static fromBase64(text:string) {
        const str = window.atob(text), len = str.length;
        const buffer = new ArrayBuffer(len)
        const ab = new Uint8Array(buffer)
        for(let i=0; i<len; i++) {
            ab[i] = str.charCodeAt(i)
        }
        return ab
    }
    static fromHex(text:string){
        const len = text.length>>1
        const buffer = new ArrayBuffer(len)
        const ab = new Uint8Array(buffer)
        for(var i=0; i<len; i+=2) {
            ab[i>>1] = parseInt(text.substring(i, i+2), 16)
        }
        return ab
    }

    static async generateKey(){
        const key = await window.crypto.subtle.generateKey({name:"RSA-OAEP", modulusLength:2048, publicExponent:new Uint8Array([1,0,1]), hash:"SHA-256"}, true, ["encrypt","decrypt"])
        const publicKey = await this.toPEM(false, key.publicKey)
        const privateKey = await this.toPEM(true, key.privateKey)
        return {privateKey, publicKey}
    }
}
export class AES {
    private _key?:CryptoKey
    private _salt:number = 32
    private _iv:number = 12
    
    async init(password:string, salt:number=32, iv:number=12) {
        const hash = await crypto.subtle.digest("SHA-256", Crypto.str2ab(password))
        this._key = await crypto.subtle.importKey("raw", hash, {name:"PBKDF2"}, false, ["deriveBits"])
        this._salt = salt
        this._iv = iv
        return this
    }

    async encrypt(text:string){
        if (!this._key) throw "AES not initialized"
        const salt = this._salt ? crypto.getRandomValues(new Uint8Array(this._salt)) : new Uint8Array(0)
        const iv = crypto.getRandomValues(new Uint8Array(this._iv))
        const key = await this._getKey(salt.buffer, "encrypt")
        const enc = new Uint8Array(await crypto.subtle.encrypt({name:"AES-GCM", iv}, key, Crypto.str2ab(text)))
        return [Crypto.toBase64(enc.buffer), Crypto.toBase64(salt.buffer), Crypto.toBase64(iv.buffer)].join(".")
    }

    async decrypt(encrypted:string){
        if (!this._key) throw "AES not initialized"
        const [enc,salt,iv] = encrypted.split(".")
        const key = await this._getKey(Crypto.fromBase64(salt).buffer, "decrypt")
        return Crypto.ab2str(await crypto.subtle.decrypt({name:"AES-GCM", iv:Crypto.fromBase64(iv)}, key, Crypto.fromBase64(enc)))
    }

    private async _getKey(salt:BufferSource, func:"encrypt"|"decrypt"){
        if (!this._key) throw "AES not initialized"
        const bits = await crypto.subtle.deriveBits({
            "name": "PBKDF2",
            "iterations": 500000,
            "hash": {
                "name": "SHA-256"
            },
            salt
        }, this._key, 256);

        return crypto.subtle.importKey("raw", bits, {name:"AES-GCM"}, false, [func])
    }
}

export class AEScbc {
    private _key?:CryptoJS.lib.WordArray
    init(password:string) {
        this._key = CryptoJS.SHA256(password)
        return this
    }

    encrypt(data:Uint8Array) {
        const bytes = new Uint8Array(16)
        for(var i=0; i<16; i++) bytes[i] = ~~(Math.random()*256)
        const iv = CryptoJS.lib.WordArray.create(bytes)
        const encoded = CryptoJS.AES.encrypt(CryptoJS.lib.WordArray.create(data), this._key!, {iv,mode:CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7})
        const ret = CryptoJS.lib.WordArray.create()
        ret.concat(iv)
        ret.concat(encoded.ciphertext)
        return this._toArray(ret)
    }
    decrypt(data:Uint8Array) {
        const iv = CryptoJS.lib.WordArray.create(data.slice(0, 16))
        const ciphertext = CryptoJS.lib.WordArray.create(data.slice(16))
        const decrypted = CryptoJS.AES.decrypt(CryptoJS.lib.CipherParams.create({ciphertext}), this._key!, {iv,mode:CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7})
        return this._toArray(decrypted)
    }
    private _toArray(words:CryptoJS.lib.WordArray) {
        const bytes = new Uint8Array(words.sigBytes)
        for(var i=0; i<bytes.length; i++) {
            bytes[i] = (words.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
        }
        return bytes
    }
}

export class RSA {
    private _pub?:CryptoKey
    private _pri?:CryptoKey

    async init(publicKey:string,privateKey?:string){
        if (this._pub) return this

        this._pub = await crypto.subtle.importKey("spki", Crypto.fromPEM(publicKey), {
            "name":"RSA-OAEP",
            "hash":"SHA-256"
        }, true, ["encrypt"])
        
        if (privateKey) this._pri = await crypto.subtle.importKey("pkcs8", Crypto.fromPEM(privateKey), {
            name:"RSA-OAEP",
            hash:"SHA-256",
        }, true, ["decrypt"])
        return this
    }

    async encrypt(text:string, encoding:"base64"|"hex"="base64") {
        const buffer = new TextEncoder().encode(text)
        const encrypted = await window.crypto.subtle.encrypt({name:"RSA-OAEP"}, this._pub!, buffer)
        return encoding=="base64" ? Crypto.toBase64(encrypted) : Crypto.ab2hex(encrypted)
    }

    async decrypt(text:string, encoding:"base64"|"hex"="base64") {
        const buffer = encoding=="base64" ? Crypto.fromBase64(text) : Crypto.fromHex(text)
        const decrypted = await window.crypto.subtle.decrypt({name:"RSA-OAEP"}, this._pri!, buffer)
        return Crypto.ab2str(decrypted)
    }

}